// Massen-Mail an die Leads der Liste: Betreff und Text mit Platzhaltern, Vorschau für den ersten Lead, Zahl der Empfänger,
// Versand nacheinander mit Pause über das E-Mail-Modul (SMTP des Nutzers, Gesendet, Protokoll nach Close), Tageslimit,
// Ergebnis je Lead. Gesendet wird nur nach ausdrücklicher Bestätigung im Dialog.
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiFehler, fehlerText } from "../../api";
import { mailEntwuerfeApi, type MailDatei, type Unterlage } from "../../api/mailEntwuerfe";
import { massenMailApi, type MailEmpfaenger, type MailSendeErgebnis, type MailStatus, type MailVorschau, type Ziel } from "../../api/leadsNeu";
import { useAuth } from "../../auth/AuthContext";
import { komm } from "../../kommunikation/api";
import { AnhangKnopf, useAblage } from "../../kommunikation/Anhang";
import { dateiAlsBase64, groesseText, MAX_ANHAENGE, MAX_DATEI_BYTES, MAX_GESAMT_BYTES } from "../../kommunikation/logik";
import type { Vorlage } from "../../kommunikation/typen";
import { htmlZuText, istHtml } from "../../werkzeuge/html";
import { Dialog } from "../Dialog";
import { Icon } from "../Icon";
import "./smartview.css";

const PLATZHALTER: [string, string][] = [
  ["{{vorname}}", "Vorname"],
  ["{{nachname}}", "Nachname"],
  ["{{firma}}", "Firma"],
  ["{{status}}", "Status"],
  ["{{absender_vorname}}", "Mein Vorname"],
];

const PROBLEM_TEXT: Record<string, string> = {
  keine_mail: "ohne E-Mail-Adresse",
  gesperrt: "auf Blacklist",
  abgemeldet: "abgemeldet",
  kein_kontakt: "ohne bisherigen Kontakt (keine Einwilligung)",
};

type Schritt = "schreiben" | "lauf" | "ergebnis";

function warten(ms: number, abbruch: { current: boolean }): Promise<void> {
  return new Promise((ok) => {
    const ende = Date.now() + ms;
    const takt = window.setInterval(() => {
      if (abbruch.current || Date.now() >= ende) {
        window.clearInterval(takt);
        ok();
      }
    }, 250);
  });
}

export function MassenMailDialog({ offen, onSchliessen, ziel, titel }: { offen: boolean; onSchliessen: () => void; ziel: Ziel; titel: string }) {
  const [schritt, setSchritt] = useState<Schritt>("schreiben");
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [empfaenger, setEmpfaenger] = useState<MailEmpfaenger[]>([]);
  const [probleme, setProbleme] = useState<Record<string, number>>({});
  const [gekappt, setGekappt] = useState(false);
  const [status, setStatus] = useState<MailStatus | null>(null);
  const [betreff, setBetreff] = useState("");
  const [text, setText] = useState("");
  const [vorschau, setVorschau] = useState<MailVorschau | null>(null);
  const [vorschauFehler, setVorschauFehler] = useState<string | null>(null);
  const [bestaetigt, setBestaetigt] = useState(false);
  const [anhaenge, setAnhaenge] = useState<MailDatei[]>([]);
  const [anhangFehler, setAnhangFehler] = useState<string | null>(null);
  const [vorlagen, setVorlagen] = useState<Vorlage[] | null>(null);
  const [ergebnisse, setErgebnisse] = useState<MailSendeErgebnis[]>([]);
  const [fortschritt, setFortschritt] = useState({ fertig: 0, gesamt: 0 });
  const [pause, setPause] = useState<number | null>(null);
  const [abgebrochen, setAbgebrochen] = useState(false);
  const abbruch = useRef(false);
  const textfeld = useRef<HTMLTextAreaElement>(null);
  const lauf = useRef("");

  const sendbar = useMemo(() => empfaenger.filter((e) => !e.problem), [empfaenger]);
  const uebrig = status?.uebrig ?? 0;
  const anzahl = Math.min(sendbar.length, uebrig);
  const erster = sendbar[0] ?? null;

  useEffect(() => {
    if (!offen) return;
    abbruch.current = false;
    setSchritt("schreiben");
    setFehler(null);
    setBestaetigt(false);
    setErgebnisse([]);
    setAbgebrochen(false);
    setVorschau(null);
    setVorlagen(null);
    setLaedt(true);
    let aktuell = true;
    massenMailApi
      .empfaenger(ziel)
      .then((r) => {
        if (!aktuell) return;
        setEmpfaenger(r.data);
        setProbleme(r.probleme);
        setGekappt(r.gekappt);
        setStatus(r.status);
      })
      .catch((e) => aktuell && setFehler(fehlerText(e)))
      .finally(() => aktuell && setLaedt(false));
    return () => {
      aktuell = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  // Vorschau für den ersten Lead, verzögert
  useEffect(() => {
    if (!offen || !erster || !betreff.trim() || !text.trim()) {
      setVorschau(null);
      setVorschauFehler(null);
      return;
    }
    let aktuell = true;
    const zeit = window.setTimeout(() => {
      massenMailApi
        .vorschau({ lead_id: erster.lead_id, betreff, text })
        .then((v) => {
          if (aktuell) {
            setVorschau(v);
            setVorschauFehler(null);
          }
        })
        .catch((e) => aktuell && setVorschauFehler(fehlerText(e)));
    }, 500);
    return () => {
      aktuell = false;
      window.clearTimeout(zeit);
    };
  }, [offen, erster, betreff, text]);

  useEffect(() => setBestaetigt(false), [betreff, text, anhaenge]);

  // Anhänge der Massen-Mail: dieselben Dateien gehen an jeden Empfänger. Sie liegen bis zum Schließen unter dem eigenen Nutzer.
  const { nutzer } = useAuth();
  const anhaengeRef = useRef<MailDatei[]>([]);
  anhaengeRef.current = anhaenge;
  const anhangGroesse = anhaenge.reduce((n, d) => n + d.groesse, 0);
  const rechnerAnhaengen = async (liste: File[]) => {
    if (!nutzer || liste.length === 0) return;
    setAnhangFehler(null);
    if (anhaenge.length + liste.length > MAX_ANHAENGE) return setAnhangFehler(`Höchstens ${MAX_ANHAENGE} Anhänge.`);
    const zu = liste.find((f) => f.size > MAX_DATEI_BYTES);
    if (zu) return setAnhangFehler(`„${zu.name}“ ist zu groß (höchstens ${MAX_DATEI_BYTES / 1048576} MB je Datei).`);
    if (anhangGroesse + liste.reduce((n, f) => n + f.size, 0) > MAX_GESAMT_BYTES) {
      return setAnhangFehler(`Die Anhänge dürfen zusammen höchstens ${MAX_GESAMT_BYTES / 1048576} MB groß sein.`);
    }
    try {
      for (const f of liste) {
        const r = await mailEntwuerfeApi.dateiHochladen({ besitzer_art: "massenmail", besitzer_id: nutzer.id, dateiname: f.name, content_type: f.type || undefined, inhalt_base64: await dateiAlsBase64(f) });
        setAnhaenge((a) => [...a, r.datei]);
      }
    } catch (e) {
      setAnhangFehler(fehlerText(e));
    }
  };
  const unterlageAnhaengen = async (u: Unterlage) => {
    if (!nutzer) return;
    setAnhangFehler(null);
    if (anhaenge.length >= MAX_ANHAENGE) return setAnhangFehler(`Höchstens ${MAX_ANHAENGE} Anhänge.`);
    try {
      const r = await mailEntwuerfeApi.uebernehmen({ besitzer_art: "massenmail", besitzer_id: nutzer.id, quelle: "unterlagen", name: u.name });
      setAnhaenge((a) => [...a, r.datei]);
    } catch (e) {
      setAnhangFehler(fehlerText(e));
    }
  };
  const anhangWeg = (d: MailDatei) => {
    setAnhaenge((a) => a.filter((x) => x.id !== d.id));
    void mailEntwuerfeApi.dateiLoeschen(d.id).catch(() => undefined);
  };
  const ablage = useAblage((liste) => void rechnerAnhaengen(liste), schritt !== "schreiben");
  // beim Schließen die hochgeladenen Originale wegräumen (an den gesendeten Mails hängen eigene Kopien)
  useEffect(() => {
    if (offen) return;
    const rest = anhaengeRef.current;
    if (rest.length) {
      setAnhaenge([]);
      rest.forEach((d) => void mailEntwuerfeApi.dateiLoeschen(d.id).catch(() => undefined));
    }
  }, [offen]);

  const einfuegen = (marke: string) => {
    const el = textfeld.current;
    if (!el) {
      setText((t) => t + marke);
      return;
    }
    const von = el.selectionStart ?? text.length;
    const bis = el.selectionEnd ?? text.length;
    setText(text.slice(0, von) + marke + text.slice(bis));
    window.setTimeout(() => {
      el.focus();
      el.setSelectionRange(von + marke.length, von + marke.length);
    }, 0);
  };

  const vorlagenLaden = () => {
    if (vorlagen) return;
    komm
      .vorlagen("email")
      .then((r) => setVorlagen(r.data))
      .catch((e) => setFehler(fehlerText(e)));
  };
  const vorlageNehmen = (id: string) => {
    const v = vorlagen?.find((x) => x.id === id);
    if (!v) return;
    setBetreff(v.subject ?? "");
    setText(istHtml(v.body) ? htmlZuText(v.body) : v.body);
  };

  const schliessen = () => {
    abbruch.current = true;
    onSchliessen();
  };

  const senden = async () => {
    if (!bestaetigt || !anzahl) return;
    abbruch.current = false;
    lauf.current = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).slice(0, 40);
    const liste = sendbar.slice(0, anzahl);
    const alle: MailSendeErgebnis[] = [];
    setErgebnisse([]);
    setAbgebrochen(false);
    setFehler(null);
    setFortschritt({ fertig: 0, gesamt: liste.length });
    setSchritt("lauf");
    let pauseSek = status?.pause_sekunden ?? 15;
    try {
      for (let i = 0; i < liste.length; i++) {
        if (abbruch.current) throw new Error("abgebrochen");
        const e = liste[i];
        let ergebnis: MailSendeErgebnis | null = null;
        for (let versuch = 0; versuch < 4 && !ergebnis; versuch++) {
          try {
            ergebnis = await massenMailApi.senden({ lead_id: e.lead_id, betreff, text, lauf_id: lauf.current, datei_ids: anhaenge.map((d) => d.id) });
          } catch (err) {
            if (err instanceof ApiFehler && err.code === "zu_viele_anfragen" && typeof err.details.warten_sekunden === "number") {
              setPause(err.details.warten_sekunden);
              await warten(err.details.warten_sekunden * 1000, abbruch);
              setPause(null);
              if (abbruch.current) throw new Error("abgebrochen");
            } else throw err;
          }
        }
        if (!ergebnis) ergebnis = { lead_id: e.lead_id, name: e.name, email: e.email, ergebnis: "fehler", grund: "Der Server verlangt weiter eine Pause." };
        alle.push(ergebnis);
        setErgebnisse([...alle]);
        setFortschritt({ fertig: alle.length, gesamt: liste.length });
        if (ergebnis.status) {
          setStatus(ergebnis.status);
          pauseSek = ergebnis.status.pause_sekunden;
        }
        if (ergebnis.ergebnis === "gesendet" && i < liste.length - 1 && pauseSek > 0) {
          setPause(pauseSek);
          await warten(pauseSek * 1000, abbruch);
          setPause(null);
        }
      }
    } catch (err) {
      setPause(null);
      if ((err as Error).message === "abgebrochen") setAbgebrochen(true);
      else if (err instanceof ApiFehler && err.code === "tageslimit") setFehler(err.message);
      else setFehler(`Der Versand wurde angehalten: ${fehlerText(err)}`);
    }
    setSchritt("ergebnis");
  };

  const gesendet = ergebnisse.filter((e) => e.ergebnis === "gesendet").length;
  const uebersprungen = ergebnisse.filter((e) => e.ergebnis === "uebersprungen");
  const fehlerLeads = ergebnisse.filter((e) => e.ergebnis === "fehler");

  const keinKonto = status !== null && !status.konto;
  const kannSenden = bestaetigt && anzahl > 0 && !keinKonto && !!vorschau && vorschau.fehlende.length === 0;

  return (
    <Dialog
      titel={`Massen-Mail · ${titel}`}
      offen={offen}
      onSchliessen={schliessen}
      breite={700}
      fuss={
        schritt === "schreiben" ? (
          <>
            <button type="button" className="knopf" onClick={schliessen}>
              Abbrechen
            </button>
            <button type="button" className="knopf knopf--primaer" disabled={!kannSenden} onClick={() => void senden()}>
              {anzahl} {anzahl === 1 ? "Mail" : "Mails"} jetzt senden
            </button>
          </>
        ) : schritt === "lauf" ? (
          <button type="button" className="knopf" onClick={() => (abbruch.current = true)}>
            Anhalten
          </button>
        ) : (
          <button type="button" className="knopf knopf--primaer" onClick={schliessen}>
            Fertig
          </button>
        )
      }
    >
      <div className={`svd km-ablage${ablage.ueber ? " km-ablage--ueber" : ""}`} {...ablage.props}>
        {schritt === "schreiben" ? (
          <>
            {laedt ? <div className="svd__leise">Empfänger werden geprüft …</div> : null}
            {!laedt && status ? (
              <div className="svd__pruefung">
                <span>
                  <strong>{sendbar.length.toLocaleString("de-DE")}</strong> von {empfaenger.length.toLocaleString("de-DE")} Leads sind Empfänger
                  {Object.entries(probleme).length ? (
                    <span className="svd__leise">
                      {" "}
                      (nicht: {Object.entries(probleme).map(([k, n]) => `${n} ${PROBLEM_TEXT[k] ?? k}`).join(", ")})
                    </span>
                  ) : null}
                  . Heute noch {status.uebrig} von {status.tageslimit} Mails möglich, {status.pause_sekunden} s Pause zwischen den Mails.
                </span>
              </div>
            ) : null}
            {gekappt ? (
              <div className="hinweis hinweis--warnung">
                <Icon name="warnung" groesse={15} />
                <span>Die Liste ist größer als {status?.max_empfaenger ?? 500}; es werden nur die ersten Treffer berücksichtigt.</span>
              </div>
            ) : null}
            {keinKonto ? (
              <div className="hinweis hinweis--fehler">
                <Icon name="warnung" groesse={15} />
                <span>Für dich ist kein E-Mail-Konto eingetragen (Einstellungen, E-Mail-Konto). Ohne Konto geht nichts raus.</span>
              </div>
            ) : null}
            {status && status.uebrig === 0 ? (
              <div className="hinweis hinweis--warnung">
                <Icon name="warnung" groesse={15} />
                <span>Das Tageslimit ist für heute erreicht.</span>
              </div>
            ) : null}
            <div className="svd__zeile">
              <label className="feld">
                <span className="feld__label">Vorlage (optional)</span>
                <select className="eingabe" defaultValue="" onFocus={vorlagenLaden} onChange={(e) => vorlageNehmen(e.target.value)}>
                  <option value="">{vorlagen ? "– Vorlage wählen –" : "Vorlagen laden …"}</option>
                  {(vorlagen ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="feld">
                <span className="feld__label">Betreff</span>
                <input className="eingabe" value={betreff} onChange={(e) => setBetreff(e.target.value)} maxLength={300} data-autofocus />
              </label>
            </div>
            <label className="feld">
              <span className="feld__label">Text</span>
              <textarea ref={textfeld} className="eingabe svd__textfeld" value={text} onChange={(e) => setText(e.target.value)} />
            </label>
            <div className="svd__anhang">
              <AnhangKnopf
                disabled={anhaenge.length >= MAX_ANHAENGE}
                onRechner={(liste) => void rechnerAnhaengen(liste)}
                onUnterlage={(u) => void unterlageAnhaengen(u)}
              />
              {anhaenge.map((d) => (
                <span key={d.id} className="chip km-datei">
                  <span className="km-datei__name">{d.dateiname}</span> <span className="km-leise">{groesseText(d.groesse)}</span>
                  <button type="button" className="km-datei__weg" aria-label={`${d.dateiname} entfernen`} onClick={() => anhangWeg(d)}>
                    <Icon name="schliessen" groesse={11} />
                  </button>
                </span>
              ))}
              <span className="km-leise">
                {anhaenge.length ? `${groesseText(anhangGroesse)} von ${MAX_GESAMT_BYTES / 1048576} MB, geht an jeden Empfänger` : `Dateien auch hierher ziehen, bis ${MAX_DATEI_BYTES / 1048576} MB je Datei`}
              </span>
            </div>
            {anhangFehler ? <div className="km-fehlertext">{anhangFehler}</div> : null}
            <div className="svd__chips" aria-label="Platzhalter einfügen">
              {PLATZHALTER.map(([marke, name]) => (
                <button key={marke} type="button" className="svd__chip" onClick={() => einfuegen(marke)} title={marke}>
                  {name}
                </button>
              ))}
            </div>
            <div>
              <div className="feld__label">
                Vorschau{erster ? ` für ${erster.name}` : ""}
                {vorschau ? ` (an ${vorschau.an.join(", ")})` : ""}
              </div>
              {vorschau ? (
                <div className="svd__vorschau">
                  <div>
                    <strong>{vorschau.betreff}</strong>
                  </div>
                  <div className="svd__vorschau-text">{vorschau.text}</div>
                </div>
              ) : (
                <div className="svd__leise">{vorschauFehler ?? (erster ? "Betreff und Text eintragen, dann erscheint die Vorschau." : laedt ? "" : "Kein Lead mit E-Mail-Adresse in dieser Liste.")}</div>
              )}
              {vorschau && vorschau.fehlende.length ? (
                <div className="hinweis hinweis--warnung" style={{ marginTop: 8 }}>
                  <Icon name="warnung" groesse={15} />
                  <span>Bei diesem Lead fehlt: {vorschau.fehlende.join(", ")}. Leads mit leeren Platzhaltern werden nicht angeschrieben.</span>
                </div>
              ) : null}
            </div>
            <label className="svd__haken">
              <input type="checkbox" checked={bestaetigt} disabled={!vorschau} onChange={(e) => setBestaetigt(e.target.checked)} />
              <span>
                Ich habe Vorschau und Empfängerzahl geprüft und will diese Mail an {anzahl} {anzahl === 1 ? "Lead" : "Leads"} senden.
              </span>
            </label>
            <div className="svd__leise">
              Gesendet wird einzeln von deiner Adresse mit deiner Signatur, jede Mail steht in „Gesendet“ und im Verlauf des Leads. Leads ohne bisherigen Kontakt, Blacklist und Abgemeldete bekommen nichts.
            </div>
          </>
        ) : null}

        {schritt === "lauf" ? (
          <>
            <div>
              Mails werden gesendet … {fortschritt.fertig} von {fortschritt.gesamt}
            </div>
            <div className="svd__balken">
              <span style={{ width: `${(100 * fortschritt.fertig) / Math.max(1, fortschritt.gesamt)}%` }} />
            </div>
            <div className="svd__leise">{pause !== null ? `Pause bis zur nächsten Mail (bis zu ${pause} s) …` : "Bitte das Fenster offen lassen."}</div>
          </>
        ) : null}

        {schritt === "ergebnis" ? (
          <>
            <div className={`hinweis ${fehler ? "hinweis--fehler" : abgebrochen ? "hinweis--warnung" : "hinweis--ok"}`}>
              <Icon name={fehler || abgebrochen ? "warnung" : "haken"} groesse={15} />
              <span>{fehler ?? (abgebrochen ? "Der Versand wurde angehalten. Bereits gesendete Mails bleiben gesendet." : "Versand abgeschlossen.")}</span>
            </div>
            <div className="svd__pruefung">
              <span>
                <strong className="svd__ok">{gesendet}</strong> gesendet · <strong>{uebersprungen.length}</strong> übersprungen ·{" "}
                <strong className={fehlerLeads.length ? "svd__fehler" : undefined}>{fehlerLeads.length}</strong> Fehler
              </span>
            </div>
            <div className="svd__scroll">
              <table className="svd__tabelle">
                <thead>
                  <tr>
                    <th>Lead</th>
                    <th>An</th>
                    <th>Ergebnis</th>
                    <th>Grund</th>
                  </tr>
                </thead>
                <tbody>
                  {ergebnisse.map((e) => (
                    <tr key={e.lead_id}>
                      <td>{e.name}</td>
                      <td>{e.email}</td>
                      <td className={e.ergebnis === "gesendet" ? "svd__ok" : e.ergebnis === "fehler" ? "svd__fehler" : undefined}>
                        {e.ergebnis === "gesendet" ? "gesendet" : e.ergebnis === "fehler" ? "Fehler" : "übersprungen"}
                      </td>
                      <td className="svd__leise">{e.grund}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {fehler && schritt === "schreiben" ? (
          <div className="hinweis hinweis--fehler" role="alert">
            <Icon name="warnung" groesse={15} />
            <span>{fehler}</span>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
